import { useEffect, useMemo, useRef, useState } from "react";
import { Crop, Loader2, Minus, Plus, RotateCcw, RotateCw, SlidersHorizontal } from "lucide-react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const MAX_ANGLE = 45;

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

/* ---------- cor: filtros e ajustes (mesma conta na tela e na foto final) ---------- */

const PRESETS = [
  { id: "original", label: "Original", gray: 0, sepia: 0, sat: 1, con: 1, bri: 1 },
  { id: "vivid", label: "Vivid", gray: 0, sepia: 0, sat: 1.35, con: 1.1, bri: 1.02 },
  { id: "warm", label: "Warm", gray: 0, sepia: 0.28, sat: 1.15, con: 1.03, bri: 1.03 },
  { id: "fade", label: "Fade", gray: 0, sepia: 0, sat: 0.8, con: 0.85, bri: 1.1 },
  { id: "mono", label: "Mono", gray: 1, sepia: 0, sat: 1, con: 1.05, bri: 1 },
  { id: "noir", label: "Noir", gray: 1, sepia: 0, sat: 1, con: 1.35, bri: 0.95 },
];

// Junta o filtro escolhido com os controles de brilho, contraste e saturacao (-50 a +50).
function colorParams(preset, adj) {
  return {
    gray: preset.gray,
    sepia: preset.sepia,
    sat: preset.sat * (1 + adj.sat / 100),
    con: preset.con * (1 + adj.con / 100),
    bri: preset.bri * (1 + adj.bri / 100),
  };
}

function isNeutral(p) {
  const eq = (a, b) => Math.abs(a - b) < 0.0005;
  return eq(p.gray, 0) && eq(p.sepia, 0) && eq(p.sat, 1) && eq(p.con, 1) && eq(p.bri, 1);
}

// Para mostrar na tela. A ordem aqui e a mesma de applyColor.
function cssFilter(p) {
  if (isNeutral(p)) return "none";
  return `grayscale(${p.gray}) sepia(${p.sepia}) saturate(${p.sat}) contrast(${p.con}) brightness(${p.bri})`;
}

// Para a foto final: as mesmas contas dos filtros do navegador, pixel a pixel.
function applyColor(data, p) {
  const lim = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);
  const gk = 1 - p.gray;
  const sk = 1 - p.sepia;
  const s = p.sat;
  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];
    let nr;
    let ng;
    let nb;
    if (p.gray > 0) {
      nr = (0.2126 + 0.7874 * gk) * r + (0.7152 - 0.7152 * gk) * g + (0.0722 - 0.0722 * gk) * b;
      ng = (0.2126 - 0.2126 * gk) * r + (0.7152 + 0.2848 * gk) * g + (0.0722 - 0.0722 * gk) * b;
      nb = (0.2126 - 0.2126 * gk) * r + (0.7152 - 0.7152 * gk) * g + (0.0722 + 0.9278 * gk) * b;
      r = lim(nr);
      g = lim(ng);
      b = lim(nb);
    }
    if (p.sepia > 0) {
      nr = (0.393 + 0.607 * sk) * r + (0.769 - 0.769 * sk) * g + (0.189 - 0.189 * sk) * b;
      ng = (0.349 - 0.349 * sk) * r + (0.686 + 0.314 * sk) * g + (0.168 - 0.168 * sk) * b;
      nb = (0.272 - 0.272 * sk) * r + (0.534 - 0.534 * sk) * g + (0.131 + 0.869 * sk) * b;
      r = lim(nr);
      g = lim(ng);
      b = lim(nb);
    }
    if (s !== 1) {
      nr = (0.213 + 0.787 * s) * r + (0.715 - 0.715 * s) * g + (0.072 - 0.072 * s) * b;
      ng = (0.213 - 0.213 * s) * r + (0.715 + 0.285 * s) * g + (0.072 - 0.072 * s) * b;
      nb = (0.213 - 0.213 * s) * r + (0.715 - 0.715 * s) * g + (0.072 + 0.928 * s) * b;
      r = lim(nr);
      g = lim(ng);
      b = lim(nb);
    }
    if (p.con !== 1) {
      r = lim(p.con * (r - 127.5) + 127.5);
      g = lim(p.con * (g - 127.5) + 127.5);
      b = lim(p.con * (b - 127.5) + 127.5);
    }
    if (p.bri !== 1) {
      r = lim(r * p.bri);
      g = lim(g * p.bri);
      b = lim(b * p.bri);
    }
    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
  }
}

/* ---------- geometria: girar livremente e nunca deixar um vazio dentro do quadro ---------- */

// iw/ih = tamanho da foto, fw/fh = quadro de recorte, theta = giro em graus, zoom >= 1.
function geometry({ iw, ih, fw, fh, theta, zoom }) {
  const t = (theta * Math.PI) / 180;
  const c = Math.cos(t);
  const s = Math.sin(t);
  const corners = [
    [-fw / 2, -fh / 2],
    [fw / 2, -fh / 2],
    [fw / 2, fh / 2],
    [-fw / 2, fh / 2],
  ];
  // cantos do quadro, vistos nos eixos da propria foto (a foto girada)
  const u = corners.map(([x, y]) => [c * x + s * y, -s * x + c * y]);
  const base = Math.max(...u.map(([ux, uy]) => Math.max(Math.abs(ux) / (iw / 2), Math.abs(uy) / (ih / 2))));
  const scale = base * zoom;
  const A = (iw * scale) / 2;
  const B = (ih * scale) / 2;
  const xs = u.map((p) => p[0]);
  const ys = u.map((p) => p[1]);
  return {
    scale,
    c,
    s,
    qx: [Math.max(...xs) - A, Math.min(...xs) + A],
    qy: [Math.max(...ys) - B, Math.min(...ys) + B],
  };
}

// Mantem o deslocamento da foto dentro do que ainda cobre o quadro inteiro.
function clampPos(p, g) {
  const qx = clamp(g.c * p.x + g.s * p.y, g.qx[0], g.qx[1]);
  const qy = clamp(-g.s * p.x + g.c * p.y, g.qy[0], g.qy[1]);
  return { x: g.c * qx - g.s * qy, y: g.s * qx + g.c * qy };
}

/* ---------- pecas visuais ---------- */

// Regua com tracinhos, como a do LinkedIn. O controle de verdade e um slider invisivel por cima.
function Ruler({ value, min, max, step = 0.01, onChange, label, ariaLabel, centered = false }) {
  const TICKS = 27;
  const pct = (value - min) / (max - min);
  return (
    <div className="relative h-16 flex-1">
      <div
        className="absolute top-0 -translate-x-1/2 whitespace-nowrap text-center text-lg font-medium"
        style={{ left: `${6 + pct * 88}%` }}
      >
        {label}
      </div>
      <div className="absolute inset-x-0 bottom-2 flex h-8 items-end justify-between">
        {Array.from({ length: TICKS }).map((_, i) => {
          const t = i / (TICKS - 1);
          const filled = centered ? (t >= Math.min(0.5, pct) - 1e-6 && t <= Math.max(0.5, pct) + 1e-6) : t <= pct + 1e-6;
          const tall = i % 5 === 0;
          return (
            <span
              key={i}
              className={`w-[3px] rounded-full ${tall ? "h-8" : "h-5"} ${filled ? "bg-emerald-400" : "bg-white/60"}`}
            />
          );
        })}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={ariaLabel}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </div>
  );
}

function RoundBtn({ onClick, label, children, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid h-12 w-12 shrink-0 place-items-center rounded-full text-white active:bg-white/10 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function ModeBtn({ active, onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={`grid h-14 w-14 place-items-center rounded-full transition-colors ${active ? "bg-white text-black" : "text-white active:bg-white/10"}`}
    >
      {children}
    </button>
  );
}

// Icone de "filtros": dois circulos que se cruzam.
function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="12" r="6" />
      <circle cx="15" cy="12" r="6" />
    </svg>
  );
}

/* ---------- o recortador ---------- */

// Tela para enquadrar a foto, no estilo do LinkedIn: arraste para mover, use dois dedos (ou a regua)
// para dar zoom, gire, escolha o formato, ajuste a cor ou aplique um filtro.
// No fim devolve a imagem ja pronta (um Blob).
//   shape:   "round" (foto de perfil) ou "square" (logo)
//   aspects: opcional, lista de { label, value } para escolher o formato (ex: Quadrado, Largo)
export default function ImageCropper({
  src,
  shape = "round",
  aspect = 1,
  aspects = null,
  title = "Crop",
  outputWidth = 512,
  outputType = "image/jpeg",
  onCancel,
  onDone,
}) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [angle, setAngle] = useState(0);
  const [quarter, setQuarter] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [ratio, setRatio] = useState(aspects && aspects.length ? aspects[0].value : aspect);
  const [mode, setMode] = useState("crop");
  const [tab, setTab] = useState("zoom");
  const [presetId, setPresetId] = useState("original");
  const [adj, setAdj] = useState({ bri: 0, con: 0, sat: 0 });
  const [stage, setStage] = useState({ w: 320, h: 420 });
  const [busy, setBusy] = useState(false);

  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);

  const theta = quarter * 90 + angle;
  const preset = PRESETS.find((p) => p.id === presetId) || PRESETS[0];
  const color = colorParams(preset, adj);

  /* tamanho da area e do quadro de recorte */
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const frameW = Math.max(120, Math.min(stage.w - 2, (stage.h - 8) * ratio));
  const frameH = frameW / ratio;

  const geo = useMemo(
    () => (img ? geometry({ iw: img.w, ih: img.h, fw: frameW, fh: frameH, theta, zoom }) : null),
    [img, frameW, frameH, theta, zoom]
  );
  const scale = geo ? geo.scale : 1;

  // Mantem a foto sempre cobrindo o quadro (ao dar zoom, girar ou trocar o formato).
  useEffect(() => {
    if (geo) setPos((p) => clampPos(p, geo));
  }, [geo]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  /* gestos: arrastar (1 dedo) e pinca (2 dedos) */
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom };
    }
  };

  const onPointerMove = (e) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev || !geo) return;
    const now = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, now);

    if (pointers.current.size >= 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      setZoom(clamp(pinch.current.zoom * (dist / pinch.current.dist), MIN_ZOOM, MAX_ZOOM));
      return;
    }
    const dx = now.x - prev.x;
    const dy = now.y - prev.y;
    setPos((p) => clampPos({ x: p.x + dx, y: p.y + dy }, geo));
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  const onWheel = (e) => {
    setZoom((z) => clamp(z - e.deltaY * 0.002, MIN_ZOOM, MAX_ZOOM));
  };

  const reset = () => {
    setZoom(1);
    setAngle(0);
    setQuarter(0);
    setPos({ x: 0, y: 0 });
    setPresetId("original");
    setAdj({ bri: 0, con: 0, sat: 0 });
  };

  const done = () => {
    if (!img || !imgRef.current || !geo || busy) return;
    setBusy(true);
    try {
      const outW = outputWidth;
      const outH = Math.round(outputWidth / ratio);
      const k = outW / frameW;
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (outputType === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, outW, outH);
      }
      ctx.translate(outW / 2 + pos.x * k, outH / 2 + pos.y * k);
      ctx.rotate((theta * Math.PI) / 180);
      ctx.scale(scale * k, scale * k);
      ctx.drawImage(imgRef.current, -img.w / 2, -img.h / 2);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (!isNeutral(color)) {
        const px = ctx.getImageData(0, 0, outW, outH);
        applyColor(px.data, color);
        ctx.putImageData(px, 0, 0);
      }
      canvas.toBlob(
        (blob) => {
          if (blob) onDone(blob);
          else setBusy(false);
        },
        outputType,
        0.92
      );
    } catch (e) {
      setBusy(false);
    }
  };

  const tabs = [
    { id: "zoom", label: "Zoom" },
    { id: "rotate", label: "Rotate" },
    { id: "aspect", label: "Aspect Ratio" },
  ];
  const adjustRows = [
    { key: "bri", label: "Brightness" },
    { key: "con", label: "Contrast" },
    { key: "sat", label: "Saturation" },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-[#1b2128] text-white"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Topo */}
      <div className="relative flex h-16 items-center justify-center px-3">
        <button
          type="button"
          onClick={reset}
          aria-label="Reset"
          className="absolute left-3 grid h-12 w-12 place-items-center rounded-full active:bg-white/10"
          data-testid="crop-reset"
        >
          <RotateCcw className="h-6 w-6" />
        </button>
        <div className="text-2xl font-semibold">Crop</div>
      </div>

      {/* Area da foto */}
      <div
        ref={stageRef}
        className="relative min-h-0 flex-1 touch-none select-none overflow-hidden"
        style={{ touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          onLoad={(e) => setImg({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={
            img
              ? {
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: img.w,
                  height: img.h,
                  maxWidth: "none",
                  marginLeft: -img.w / 2,
                  marginTop: -img.h / 2,
                  transformOrigin: "center",
                  transform: `translate(${pos.x}px, ${pos.y}px) rotate(${theta}deg) scale(${scale})`,
                  filter: cssFilter(color),
                  pointerEvents: "none",
                }
              : { opacity: 0, position: "absolute" }
          }
        />

        {!img && (
          <div className="absolute inset-0 grid place-items-center">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" />
          </div>
        )}

        {/* Quadro de recorte: o que fica fora dele escurece */}
        {img && (
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 border border-white/70"
            style={{
              width: frameW,
              height: frameH,
              marginLeft: -frameW / 2,
              marginTop: -frameH / 2,
              borderRadius: shape === "round" ? "9999px" : "6px",
              boxShadow: "0 0 0 9999px rgba(20,24,29,0.55)",
              overflow: "hidden",
            }}
          >
            <div className="absolute inset-y-0 left-1/3 w-px bg-white/45" />
            <div className="absolute inset-y-0 left-2/3 w-px bg-white/45" />
            <div className="absolute inset-x-0 top-1/3 h-px bg-white/45" />
            <div className="absolute inset-x-0 top-2/3 h-px bg-white/45" />
          </div>
        )}
      </div>

      {/* Ferramentas */}
      <div className="shrink-0 pb-1 pt-3">
        {mode === "crop" && (
          <>
            <div className="flex gap-7 overflow-x-auto px-6 pb-2" role="tablist" aria-label="Crop tools">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={`shrink-0 whitespace-nowrap text-xl ${tab === t.id ? "font-semibold text-white" : "text-white/55"}`}
                  data-testid={`crop-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex h-20 items-center gap-2 px-3">
              {tab === "zoom" && (
                <>
                  <RoundBtn label="Zoom out" onClick={() => setZoom((z) => clamp(z - 0.25, MIN_ZOOM, MAX_ZOOM))}>
                    <Minus className="h-6 w-6" />
                  </RoundBtn>
                  <Ruler value={zoom} min={MIN_ZOOM} max={MAX_ZOOM} onChange={setZoom} label={`${zoom.toFixed(1)}x`} ariaLabel="Zoom" />
                  <RoundBtn label="Zoom in" onClick={() => setZoom((z) => clamp(z + 0.25, MIN_ZOOM, MAX_ZOOM))}>
                    <Plus className="h-6 w-6" />
                  </RoundBtn>
                </>
              )}

              {tab === "rotate" && (
                <>
                  <RoundBtn label="Rotate left 90 degrees" onClick={() => setQuarter((q) => (q + 3) % 4)}>
                    <RotateCcw className="h-6 w-6" />
                  </RoundBtn>
                  <Ruler
                    value={angle}
                    min={-MAX_ANGLE}
                    max={MAX_ANGLE}
                    step={0.5}
                    onChange={setAngle}
                    label={`${Math.round(angle)}°`}
                    ariaLabel="Straighten"
                    centered
                  />
                  <RoundBtn label="Rotate right 90 degrees" onClick={() => setQuarter((q) => (q + 1) % 4)}>
                    <RotateCw className="h-6 w-6" />
                  </RoundBtn>
                </>
              )}

              {tab === "aspect" && (
                <div className="flex w-full flex-wrap items-center justify-center gap-2 px-3">
                  {aspects && aspects.length > 1 ? (
                    aspects.map((a) => (
                      <button
                        key={a.label}
                        type="button"
                        onClick={() => setRatio(a.value)}
                        className={`h-11 rounded-full border px-5 text-base font-medium ${
                          ratio === a.value ? "border-white bg-white text-black" : "border-white/30 text-white/80"
                        }`}
                      >
                        {a.label}
                      </button>
                    ))
                  ) : (
                    <span className="text-center text-base text-white/70">
                      {shape === "round" ? "Profile photos are round (1:1)." : "This format is fixed."}
                    </span>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {mode === "adjust" && (
          <div className="space-y-1 px-6 pb-2 pt-1">
            {adjustRows.map((row) => (
              <div key={row.key} className="flex items-center gap-4">
                <div className="w-28 shrink-0 text-lg">{row.label}</div>
                <input
                  type="range"
                  min={-50}
                  max={50}
                  step={1}
                  value={adj[row.key]}
                  onChange={(e) => setAdj((a) => ({ ...a, [row.key]: Number(e.target.value) }))}
                  aria-label={row.label}
                  className="h-12 flex-1 accent-white"
                />
                <div className="w-10 text-right font-mono text-sm text-white/70">{adj[row.key]}</div>
              </div>
            ))}
          </div>
        )}

        {mode === "filters" && (
          <div className="flex gap-3 overflow-x-auto px-5 pb-2 pt-1">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPresetId(p.id)}
                aria-pressed={presetId === p.id}
                className="shrink-0 text-center"
                data-testid={`filter-${p.id}`}
              >
                <span
                  className={`block h-[72px] w-[72px] overflow-hidden rounded-xl ${
                    presetId === p.id ? "ring-2 ring-white" : "ring-1 ring-white/20"
                  }`}
                >
                  <img
                    src={src}
                    alt=""
                    draggable={false}
                    className="h-full w-full object-cover"
                    style={{ filter: cssFilter(colorParams(p, { bri: 0, con: 0, sat: 0 })) }}
                  />
                </span>
                <span className={`mt-1 block text-sm ${presetId === p.id ? "font-semibold text-white" : "text-white/60"}`}>{p.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cancelar, ferramentas e Concluir */}
      <div className="flex items-center justify-between px-5 pb-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="h-12 min-w-[84px] text-left text-xl font-medium active:opacity-70 disabled:opacity-50"
        >
          Cancel
        </button>
        <div className="flex items-center gap-2">
          <ModeBtn label="Crop" active={mode === "crop"} onClick={() => setMode("crop")}>
            <Crop className="h-7 w-7" />
          </ModeBtn>
          <ModeBtn label="Adjust" active={mode === "adjust"} onClick={() => setMode("adjust")}>
            <SlidersHorizontal className="h-7 w-7" />
          </ModeBtn>
          <ModeBtn label="Filters" active={mode === "filters"} onClick={() => setMode("filters")}>
            <FilterIcon />
          </ModeBtn>
        </div>
        <button
          type="button"
          onClick={done}
          disabled={!img || busy}
          className="flex h-12 min-w-[84px] items-center justify-end gap-2 text-xl font-semibold active:opacity-70 disabled:opacity-50"
          data-testid="crop-done"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" />}
          Done
        </button>
      </div>
    </div>
  );
}
